/**
 * Por qué terminó una sesión, para poder decírselo a quien vuelve a entrar.
 *
 * ## El defecto que esto corrige
 *
 * Al arrancar, el portal lee la ficha de quien Firebase recuerda en el
 * navegador. Si esa lectura fallaba —un token caducado tras una noche con el
 * portátil cerrado, o la red todavía reconectando al despertarlo— la sesión se
 * resolvía como `pending-validation`, y la pantalla le decía a un superusuario
 * activo que su cuenta «está en camino» y que RR.HH. tenía que validarla.
 *
 * Con las claims intactas —rol, ficha y estado activo—, ése era el único camino a
 * esa pantalla. No era un texto mal escrito: era un estado falso, y cualquier
 * redacción encima habría seguido mintiendo.
 *
 * ## Por qué se guarda en el navegador
 *
 * El cierre por inactividad y el de token caducado terminan en `signOut`, que
 * deja la aplicación en la pantalla de acceso sin memoria de cómo llegó ahí. Sin
 * una marca, esa pantalla no puede distinguir «cerraste sesión» de «te la
 * cerramos». En `localStorage` y no en memoria porque el cierre por inactividad
 * es compartido entre pestañas, y porque suele leerse después de recargar.
 */

export type SessionEndReason = 'idle' | 'expired';

const STORAGE_KEY = 'knpeople.sessionEnded';

/**
 * Deja constancia de que la sesión la cerró el portal, no la persona.
 *
 * Tolerante a un navegador que bloquea el almacenamiento: perder el aviso es
 * aceptable; romper el cierre de sesión por no poder escribirlo, no.
 */
export const markSessionEnded = (reason: SessionEndReason): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, reason);
  } catch {
    /* Sin almacenamiento no hay aviso, pero la sesión se cierra igual. */
  }
};

export const readSessionEnded = (): SessionEndReason | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === 'idle' || raw === 'expired' ? raw : null;
  } catch {
    return null;
  }
};

/**
 * Se borra al volver a entrar. Si no, el aviso reaparecería la próxima vez que
 * la persona cierre sesión por su cuenta, contándole algo que ya no pasó.
 */
export const clearSessionEnded = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Nada que limpiar si no hay almacenamiento. */
  }
};

/** Qué hacer cuando las claims dicen «activa» pero la ficha no se pudo leer. */
export type UnreadableRecordOutcome = 'expired' | 'unreachable' | 'pending';

/**
 * Traduce el fallo de lectura de la propia ficha a lo que de verdad significa.
 *
 * - **`permission-denied`** — incluye el token caducado: `translateFirestoreError`
 *   clasifica `unauthenticated` aquí a propósito. La sesión ya no sirve, así que
 *   se cierra y se explica. Dejarla abierta sería una aplicación a medio pintar
 *   que rechaza cada lectura.
 * - **`unavailable`** — la red. Nada se sabe de la cuenta, así que no se afirma
 *   nada de ella: se ofrece reintentar.
 * - **`not-found`** — la ficha de verdad no está: una baja a medias, lo más
 *   probable. Es el único caso para el que «habla con RR.HH.» es cierto, y el
 *   único que se queda como estaba.
 *
 * **Lo que no se reconoce cae en `unreachable`**, no en `pending`. Equivocarse
 * hacia «reintenta» cuesta un clic; equivocarse hacia «tu cuenta no está
 * validada» es exactamente la pantalla que se vino a quitar.
 */
export const outcomeForUnreadableRecord = (errorKind: string): UnreadableRecordOutcome => {
  switch (errorKind) {
    case 'permission-denied':
      return 'expired';
    case 'not-found':
      return 'pending';
    default:
      return 'unreachable';
  }
};
